<template>
  <div
    :class="componentClass"
    :style="componentStyle"
  >
    <owa-tag
      v-if="shouldShowSaleTag || isPlusOnSale"
      theme="yellow"
      label="On Sale!"
      position="top"
      size="size-sm"
    />

    <owa-tag
      v-else-if="isExpiredActivePlan && !isInModalContext"
      theme="gray inverted"
      label="Your Expired Plan"
      position="top"
      size="size-sm"
    />

    <owa-tag
      v-else-if="isCurrentPlan && !isInModalContext"
      theme="blue"
      :label="useProCardForLegacyPlan && currentSubName
        ? `Your Plan · ${currentSubName}`
        : 'Your Plan'"
      position="top"
      size="size-sm"
    />

    <owa-tag
      v-else-if="plan.mostPopular && !shouldShowSaleTag && !isDowngrade && !(isInModalContext && isSinglePlanModal)"
      theme="yellow"
      label="Most Popular"
      position="top"
      size="size-sm"
    />

    <owa-tag
      v-else-if="plan.sku === 'FA-STANDARD-PLUS' && !shouldShowSaleTag && shouldShowBestValueTag && !isDowngrade && !hasActivePaidSubscription && isInModalContext"
      theme="teal"
      label="Best Value"
      position="top"
      size="size-sm"
    />

    <owa-tag
      v-else-if="!plan.proDownloadEnabled && (!isInModalContext || isSinglePlanModal)"
      theme="gravy"
      label="Online Only"
      position="top"
      size="size-sm"
    />

    <h2 class="h1 margin-bottom-2xs">
      <span :style="`color: var(--fa-${planColor});`">
        <font-awesome-icon
          :icon="$v7[planIconStyle][icon]"
          size="lg"
          class="decorative fa-width-auto"
        />
      </span>
      <span class="size-md tablet:size-sm laptop:size-md tablet:display-block laptop:display-inline"
        ><span v-if="shortName">{{ shortName }}</span
        ><span v-else>{{ activePlan.name }}</span></span
      >
    </h2>

    <div style="flex-grow: 1; flex-shrink: 0; flex-basis: auto">
      <template v-if="isInModalContext">
        <h3 class="sr-only">This Plan Includes&hellip;</h3>
        <p
          v-if="planDescriptionModal"
          class="margin-bottom-md size-lg"
          style="color: var(--fa-md-gravy)"
        >
          {{ planDescriptionModal }}
        </p>
        <ul
          class="size-lg fa-ul margin-y-0 margin-left-xl"
        >
          <li class="b">
            <span class="fa-li">
              <font-awesome-icon
                :icon="$v7.solid.faCheck"
                size="sm"
              />
            </span>
            <template v-if="modalContextSlug.includes('custom_icon') || modalContextSlug.includes('using_the_icon_wizard')">              <template v-if="plan.customIcons === -1">
                Unlimited
              </template>
              <template v-else>
                Includes {{ plan.customIcons }}
              </template>
              custom icons
            </template>
            <template v-else-if="modalContextSlug.includes('download')">
              <template v-if="planHasProPlusIcons">Download Pro & Pro+ SVGs, fonts, and assets</template>
              <template v-else>Download Pro SVGs, fonts, and assets</template>
            </template>
            <template v-else-if="modalContextSlug.includes('using_this_icon')">
              <template v-if="planHasProPlusIcons">Use Pro & Pro+ icons in projects</template>
              <template v-else>Use Pro icons in projects</template>
            </template>
            <template v-else-if="modalContextSlug.includes('pro_icon')">
              Unlock Pro Icons in Kits
            </template>
            <template v-else-if="modalContextSlug.includes('removing_advertisements')">
              Ad-free experience
            </template>
            <template v-else-if="modalContextSlug.includes('service')">
              Unlock Additional Services
            </template>
            <template v-else>
              Unlock this feature
            </template>
          </li>
        </ul>
      </template>

      <template v-else>
        <h3 class="sr-only">About This Plan</h3>

        <p
          v-if="planDescriptionPrimary"
          class="margin-bottom-md size-lg"
        >
          {{ planDescriptionPrimary }}
        </p>
        <p
          v-else
          class="margin-bottom-0"
        >
          {{ plan.description }}
        </p>

        <p
          v-if="planDescriptionSecondary"
          style="color: var(--fa-md-gravy)"
        >
          {{ planDescriptionSecondary }}
        </p>
      </template>
    </div>

    <div>
      <template v-if="isShowingDiscount">
        <div
          class="row align-middle align-center margin-y-sm"
          style="gap: var(--spacing-xs); --grid-gutter-x-width: 0; --padding-left: 0; --padding-right: 0;"
        >
          <div
            v-if="currentDollars !== planPrice"
            class="strong text-center"
            style="color: var(--fa-gravy);"
          >
            <span class="sr-only">Regular price:</span>
            <span class="size-4xl tablet:size-3xl laptop:size-4xl"
              ><s>{{ planPrice }}<span class="sr-only">/year</span></s></span
            >
          </div>
          <div
            class="strong"
          >
            <span class="sr-only">Current price:</span>
            <span class="display-flex align-start align-center">
              <span class="size-4xl tablet:size-3xl laptop:size-4xl">{{ currentDollars }}</span>
              <span
                v-if="currentCents"
                class="size-lg padding-top-2xs"
                >.{{ currentCents }}</span
              >
            </span>
            <span class="sr-only"
              >{{ priceDurationLabel }}<span v-if="discountDuration === 'once'">, then {{ planPrice }}/year</span></span
            >
          </div>
        </div>
      </template>

      <div
        v-else
        class="text-center"
      >
        <p class="size-4xl strong margin-y-4xs">
          {{ currentDollars }}<span v-if="currentCents">.{{ currentCents }}</span>
        </p>
      </div>

      <template v-if="isShowingCheckbox && !isInModalContext">
        <input
          :id="`plus-addon-${plan.sku}`"
          type="checkbox"
          class="with-icon"
          :checked="plusChecked"
          @change="plusChecked = $event.target.checked"
        />
        <label
          :for="`plus-addon-${plan.sku}`"
          class="display-flex flex-items-center gap-sm padding-sm margin-top-xl margin-bottom-xl"
          :style="{
            '--label-display': 'flex',
            width: '100%',
            background: plusChecked ? 'var(--fa-lt-gravy)' : 'var(--white)',
            border: '2px solid var(--fa-lt-gravy)',
            borderRadius: 'var(--border-radius-sm)',
            cursor: 'pointer',
            'font-weight': 'var(--font-weight-normal)'
          }"
        >
          <span class="position-relative" style="flex-shrink: 0; color: var(--fa-navy);">
            <font-awesome-icon :icon="$v7.regular.faSquare" size="lg" class="unchecked-icon" />
            <font-awesome-icon :icon="$v7.regular.faSquare" size="lg" class="hover-icon" />
            <font-awesome-icon :icon="$v7.solid.faSquareCheck" size="lg" class="checked-icon" />
          </span>
          <span class="size-md padding-left-xs">
            <template v-if="plusChecked">Added Pro+ Icons</template>
            <template v-else>Add Pro+ Icons</template>
          </span>
          <span
            v-if="!plusChecked && checkboxHelperText"
            class="size-sm muted margin-left-auto tablet:display-none laptop:display-inline"
          >{{ checkboxHelperText }}</span>
        </label>
      </template>

      <span
        v-if="isCurrentPlan && !isExpiredPlan"
        :class="`button flat size-lg block margin-y-0 ${planColor} inverted`"
        style="pointer-events: none"
        >Your Plan!</span
      >

      <site-link
        v-else-if="isDowngrade"
        :class="`button flat with-border size-lg block margin-y-0`"
        :to="planLinkArguments(plan)"
        @click.native="trackThen($event, isInModalContext)"
      >
        Downgrade to {{ plan.name }}
      </site-link>

      <site-link
        v-if="(!isCurrentPlan || isExpiredPlan) && !isDowngrade"
        :class="`button size-lg block margin-bottom-0 ${planColor}`"
        :to="planLinkArguments(activePlan)"
        @click.native="trackThen($event, isInModalContext)"
      >
        <template v-if="hasActivePaidSubscription">Upgrade to {{ activePlan.name }}</template>
        <template v-else>Get {{ activePlan.name }}</template>
      </site-link>

      <p
        class="text-center"
        style="color: var(--fa-md-gravy); padding-top: var(--spacing-2xl); margin-bottom: calc(-1 * var(--spacing-md))"
        :aria-label="priceDurationDescription"
        data-balloon-pos="down"
        data-balloon-length="fit"
      >
        <font-awesome-icon
          :icon="$v7.solid.faCalendarDays"
          size="lg"
          class="decorative margin-right-2xs"
        />
        {{ priceDurationLabel }}
      </p>

    </div>
  </div>
</template>

<script>
  import accounting from 'accounting'
  import { get } from 'lodash'
  import { mapGetters, mapActions } from 'vuex'
  import {
    shouldHavePromoCode,
    getDiscountPercent,
    PLAN_COLOR_BY_SKU,
    PLAN_ICONSTYLE_BY_SKU,
    PLAN_DESCRIPTION_BY_SKU,
    PLAN_DESCRIPTION_PRIMARY_BY_SKU,
    PLAN_DESCRIPTION_SECONDARY_BY_SKU,
    getAutoAppliedDiscounts,
    getKickstarterDiscounts,
    getPromoCodes
  } from '../PlansData.js'
  import { EventTrackingMixin } from 'app/mixins'
  import UtilsMixin from 'app/views/UtilsMixin'
  import { priceWithDiscount, doesDiscountApplyToPlan, formatTotalCurrency, discountRepeatingYears } from 'app/views/plans/priceUtils'

  export default {
    name: 'PlanSummaryCard',

    mixins: [UtilsMixin, EventTrackingMixin],

    props: {
      customStyle: {
        type: Object,
        default: () => {}
      },
      modalContextSlug: {
        type: String
      },
      hasBorder: {
        type: Boolean,
        default: false
      },
      isInModalContext: {
        type: Boolean,
        default: false
      },
      isSinglePlanModal: {
        type: Boolean,
        default: false
      },
      isLast: {
        type: Boolean,
        default: true
      },
      isShowingTag: {
        type: Boolean,
        default: true
      },
      isShowingDescription: {
        type: Boolean,
        default: true
      },
      sku: {
        type: String
      },
      shortName: {
        type: String
      },
      hasNotOfferedPlan: {
        type: Boolean,
        default: false
      },
      hasActiveOldPlan: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        currentDiscount: null,
        isShowingAboutProPlusModal: false,
        plan: null,
        plusChecked: false
      }
    },

    computed: {
      ...mapGetters('accounts', ['currentAccount']),
      ...mapGetters('plans', ['currentPlans', 'getPlan', 'subscriptionDiscounts']),
      ...mapGetters('subscriptions', ['currentSubscription']),
      ...mapGetters('discounts', ['globalDiscountCode']),

      plusSku() {
        const map = {
          'FA-LITE': 'FA-LITE-PLUS',
          'FA-STANDARD': 'FA-STANDARD-PLUS',
          'FA-MAX': 'FA-MAX-PLUS'
        }
        return map[this.plan?.sku] || null
      },

      plusPlan() {
        return this.plusSku ? this.getPlan(this.plusSku) : null
      },

      activePlan() {
        return (this.plusChecked && this.plusPlan) ? this.plusPlan : this.plan
      },

      buyRoute() {
        return `plans.${this.activePlan.slug}`
      },

      componentClass() {
        return [
          'plan-card-summary card roomy text-left display-flex flex-column padding-top-2xl with-top-tag with-border',
          { 'margin-bottom-3xl tablet:margin-bottom-0': !this.isLast && !this.isInModalContext },
          { 'margin-bottom-3xl laptop:margin-bottom-0': !this.isLast && this.isInModalContext },
          { 'plan-on-sale': this.shouldShowSaleTag },
          { 'plan-current': this.isCurrentPlan && !this.isExpiredPlan },
          {
            'plan-featured':
              this.plan?.mostPopular &&
              this.plan?.sku !== 'FA-STANDARD-PLUS' &&
              !(this.isInModalContext && this.isSinglePlanModal)
          },
          {
            'plan-best-value':
              this.plan?.sku === 'FA-STANDARD-PLUS' &&
              !this.shouldShowSaleTag &&
              this.shouldShowBestValueTag &&
              !this.hasActivePaidSubscription &&
              !(this.isInModalContext && this.isSinglePlanModal)
          }

         ]
      },

      planDescriptionModal() {
        if (!this.isInModalContext) return null
        return get(PLAN_DESCRIPTION_BY_SKU, [this.plan?.sku], null)
      },

      planDescriptionPrimary() {
        if (this.isInModalContext) return null
         return get(PLAN_DESCRIPTION_PRIMARY_BY_SKU, [this.plan?.sku], null)
      },

      planDescriptionSecondary() {
        if (this.isInModalContext) return null
        if (this.isShowingCheckbox) return null
        return get(PLAN_DESCRIPTION_SECONDARY_BY_SKU, [this.plan?.sku], null)
      },

      currentSubName() {
        return get(this.currentSubscription, ['plan', 'name'], null)
      },

      hasActivePaidSubscription() {
        const status = get(this.currentSubscription, ['status'], null)
        const type = get(this.currentSubscription, ['type'], null)

        return status === 'active' && type !== 'free'
      },

      discountPercent() {
        return getDiscountPercent(this.currentAccount, this.currentSubscription)
      },

      componentStyle() {
        let style = {}

        style['--card-roomy-vertical-padding'] = 'var(--spacing-2xl)'
        style['--card-roomy-horizontal-padding'] = 'var(--spacing-2xl)'
        style['--card-margin-bottom'] = '0'
        style['flex'] = '1'

        if (this.isInModalContext) {
          style['--border-width'] = 'var(--border-width-sm)'
        }

        return {
          ...style,
          ...this.customStyle
        }
      },

      icon() {
        return get(this.plan, ['iconName'], 'faMoon')
      },

      planColor() {
        return get(PLAN_COLOR_BY_SKU, [this.plan.sku], 'yellow')
      },

      planIconStyle() {
        return get(PLAN_ICONSTYLE_BY_SKU, [this.plan.sku], 'solid')
      },

      isCurrentPlan() {
        const currentPlanSku = get(this.currentSubscription, ['plan', 'sku'], null)

        return this.plan.sku === currentPlanSku || this.useProCardForLegacyPlan
      },

      useProCardForLegacyPlan() {
        // For legacy/no-longer-offered PAID subscriptions, treat the Pro card as the "current plan"
        // (legacy plans are Pro-equivalent but have different SKUs/pricing).
        if (!this.hasActivePaidSubscription) return false

        if (!(this.hasNotOfferedPlan || this.hasActiveOldPlan)) return false

        return this.plan?.sku === 'FA-STANDARD'
      },

      displayPlanPriceAmount() {
        // Default to the active plan's price, but for legacy-equivalent Pro display the subscriber's active price.
        if (this.useProCardForLegacyPlan) {
          return get(this.currentSubscription, ['plan', 'price', 'amount'], get(this.plan, ['price', 'amount'], 0))
        }

        return get(this.activePlan, ['price', 'amount'], 0)
      },

      isDowngrade() {
        const currentPlan = get(this.currentSubscription, ['plan'], null)
        const currentSubscriptionStatus = get(this.currentSubscription, ['status'], null)

        if (currentSubscriptionStatus === 'canceled') return false

        return currentPlan ? !get(currentPlan, ['allowedUpgrades'], []).includes(get(this.plan, ['sku'], 'NOPLAN')) : false
      },

      isExpiredPlan() {
        const currentPlanSku = get(this.currentSubscription, ['plan', 'sku'], null)
        const subStatusCancelled = get(this.$subscription, ['status']) === 'canceled'

        if (this.plan.sku === currentPlanSku && subStatusCancelled) {
          return true
        } else {
          return false
        }
      },

      isExpiredActivePlan() {
        const currentPlanSku = get(this.currentSubscription, ['plan', 'sku'], null)
        const subStatusCancelled = get(this.$subscription, ['status']) === 'canceled'
        return subStatusCancelled && this.activePlan?.sku === currentPlanSku
      },

      currentPrice() {
        let price = this.displayPlanPriceAmount

        // Only apply the discount when we're actually showing discount UI.
        // For current plans and downgrades we suppress the sale UI and should show the regular price.
        if (this.isShowingDiscount && this.currentDiscount) {
          price = priceWithDiscount(this.activePlan, price, this.currentDiscount)
        }

        return formatTotalCurrency(price / 100)
      },

      shouldSuppressDiscountUI() {
        return this.isDowngrade || (this.isCurrentPlan && !this.isExpiredPlan)
      },

      isShowingDiscount() {
        if (this.shouldSuppressDiscountUI) return false
        if (!this.currentDiscount) return false

        return doesDiscountApplyToPlan(this.activePlan, this.currentDiscount)
      },

      planHasProPlusIcons() {
        return this.plan?.iconCollection?.name === 'pro-plus-collection'
      },

      planHasProDownload() {
        return this.plan?.proDownloadEnabled
      },

      planIsOnlineOnly() {
        return !this.plan?.proDownloadEnabled
      },

      discountDuration() {
        return get(this.currentDiscount, 'duration', null)
      },

      planPrice() {
        return accounting.formatMoney(this.displayPlanPriceAmount / 100, null, 0)
      },

      currentDollars() {
        return this.currentPrice[0]
      },

      currentCents() {
        return this.currentPrice[1]
      },

      priceDurationLabel() {
        if (this.discountDuration === 'repeating') {
          return `${discountRepeatingYears(this.currentDiscount)} Year Discount`
        } else if (this.discountDuration === 'once') {
          return 'First Year Discount'
        } else if (this.discountDuration === 'forever') {
          return 'Annual Discount'
        } else {
          return 'Billed Annually'
        }
      },

      priceDurationDescription() {
        if (this.discountDuration === 'repeating') {
          return `Enjoy a special deal for ${discountRepeatingYears(this.currentDiscount)} years!`
        } else if (this.discountDuration === 'once') {
          return 'Enjoy a special deal for your first year!'
        } else if (this.discountDuration === 'forever') {
          return "Keep saving as long as you're subscribed!"
        } else {
          return 'Renews each year'
        }
      },

      shouldShowSaleTag() {
        if (this.shouldSuppressDiscountUI) return false
        if (this.plusChecked) return false

        return this.currentDiscount ? doesDiscountApplyToPlan(this.plan, this.currentDiscount) : false
      },

      isPlusOnSale() {
        if (!this.plusChecked || !this.plusPlan) return false
        if (this.shouldSuppressDiscountUI || !this.currentDiscount) return false
        return doesDiscountApplyToPlan(this.plusPlan, this.currentDiscount)
      },

      shouldShowBestValueTag() {
        if (this.plan?.sku !== 'FA-STANDARD-PLUS') return false
        const standardPlan = this.getPlan('FA-STANDARD')
        if (!standardPlan) return true

        return !this.subscriptionDiscounts.some(
          (d) => doesDiscountApplyToPlan(standardPlan, d) && !doesDiscountApplyToPlan(this.plan, d)
        )
      },

      isShowingCheckbox() {
        return !!this.plusSku && !this.hasActivePaidSubscription
      },

      checkboxHelperText() {
        const plusPlan = this.plusPlan
        if (!plusPlan) return null

        let standardAmount = get(this.plan, ['price', 'amount'], null)
        let plusAmount = get(plusPlan, ['price', 'amount'], null)
        if (!standardAmount || !plusAmount) return null

        if (!this.shouldSuppressDiscountUI && this.currentDiscount) {
          if (doesDiscountApplyToPlan(this.plan, this.currentDiscount)) {
            standardAmount = priceWithDiscount(this.plan, standardAmount, this.currentDiscount)
          }
          if (doesDiscountApplyToPlan(plusPlan, this.currentDiscount)) {
            plusAmount = priceWithDiscount(plusPlan, plusAmount, this.currentDiscount)
          }
        }

        const deltaCents = plusAmount - standardAmount
        const abs = Math.abs(deltaCents)
        const formatted = accounting.formatMoney(abs / 100, null, 0)

        if (deltaCents < 0) return `Save ${formatted}/yr`
        if (deltaCents === 0) return 'On Sale!'
        return `+${formatted}/yr`
      },

    },

    async created() {
      const data = [this.fetchPlans()]
      // Plans are already in Vuex when this component mounts — PlansView awaits fetchPlans before rendering cards
      this.plan = this.planBySku(this.sku)

      const expiredPlanSku = get(this.currentSubscription, ['plan', 'sku'], null)
      if (get(this.currentSubscription, ['status']) === 'canceled' && this.plusPlan?.sku === expiredPlanSku) {
        this.plusChecked = true
      }

      const plansToCheck = [this.plan]
      if (this.isShowingCheckbox && this.plusSku) {
        const plusPlan = this.currentPlans.find((p) => p.sku === this.plusSku)
        if (plusPlan) plansToCheck.push(plusPlan)
      }

      const setCurrentDiscount = () => {
        if (this.subscriptionDiscounts.length > 1) {
          this.currentDiscount = this.subscriptionDiscounts.find((discount) =>
            plansToCheck.some((p) => get(discount, ['appliesTo', 'products'], []).includes(p.sku))
          ) || null
        } else if (this.subscriptionDiscounts.length === 1 && plansToCheck.some((p) => doesDiscountApplyToPlan(p, this.subscriptionDiscounts[0]))) {
          this.currentDiscount = this.subscriptionDiscounts[0]
        }
      }

      setCurrentDiscount()

      if (shouldHavePromoCode(this.currentAccount, this.currentSubscription)) {
        data.push(this.fetchDiscounts([getPromoCodes()[this.discountPercent]['id']]))
        this.promoCode = getPromoCodes()[this.discountPercent]['code']
      } else if (this.globalDiscountCode) {
        data.push(this.fetchDiscounts([this.globalDiscountCode]))
      } else if (getKickstarterDiscounts().length > 0 && this.isBackerWithoutActiveSubscription) {
        data.push(this.fetchDiscounts(getKickstarterDiscounts()))
      } else if (getAutoAppliedDiscounts().length > 0) {
        data.push(this.fetchDiscounts(getAutoAppliedDiscounts()))
      }

      try {
        await Promise.all(data)
        setCurrentDiscount()
      } finally {
        this.isLoading = false
      }
    },

    methods: {
      ...mapActions('plans', ['fetchPlans', 'fetchDiscounts']),

      featureListItemClass(feature_slug) {
        return this.modalContextSlug.includes(feature_slug) ? 'b' : null
      },

      trackThen(event, isInModalContext) {
        if (isInModalContext) {
          this.purchaseFlowEvent('get_pro_button_click', {
            context: 'upsell_modal',
            feature: this.modalContextSlug
          })
        }
      },

      planBySku(sku) {
        const plan = this.currentPlans.find((plan) => plan.sku == sku)

        if (!plan) {
          throw new Error(`Cannot find a plan with SKU "${sku}" in currentPlans.`)
        }

        return plan
      },

      planLinkArguments(plan) {
        if (this.currentDiscount) {
          if (doesDiscountApplyToPlan(plan, this.currentDiscount)) {
            return { name: this.buyRoute, query: { discount: this.currentDiscount.promoCode } }
          }
        }

        return { name: this.buyRoute }
      }
    }
  }
</script>
